import type { AudioTrack } from 'bitmovin-player/types/core/PlayerAPI'
import type { SubtitleTrack } from 'bitmovin-player/types/subtitles/API'
import Hls from 'hls.js'
import { Durations } from '@/constants/durations'
import { DEFAULT_FRAME_RATE } from '@/constants/video'
import { logger } from '@/utils/logging'
import type { Asset, AssetDescription, SmpteController } from '../BitmovinPlayer/types'
import type { QualityLevel } from './types'

/**
 * Implements the SmpteController interface for the HLS.js player.
 * Wraps an HTML5 video element and HLS.js instance to provide the same
 * programmatic control surface as the Bitmovin SMPTEController.
 */
export class HlsPlayerController implements SmpteController {
  private video: HTMLVideoElement
  private hls: Hls
  private asset: Asset
  private destroyed = false

  constructor(video: HTMLVideoElement, hls: Hls, asset: Asset, autoplay?: boolean) {
    this.video = video
    this.hls = hls
    this.asset = asset
    // Source is already loaded by useHlsInstance — just handle initial position and autoplay.
    this.applyInitialState(asset, autoplay)
  }

  /**
   * Applies initial seek position and autoplay after the source has been loaded
   * by useHlsInstance. Waits for MANIFEST_PARSED if it hasn't fired yet.
   */
  private applyInitialState(asset: Asset, autoplay?: boolean): void {
    const apply = () => {
      if (asset.position && typeof asset.position === 'number') {
        this.seek(asset.position)
      }
      if (autoplay) {
        this.play()
      }
    }

    // If HLS.js has already parsed the manifest (readyState > 0), apply immediately.
    if (this.video.readyState > 0) {
      apply()
    } else {
      this.hls.once(Hls.Events.MANIFEST_PARSED, apply)
    }
  }

  private isReady(): boolean {
    return !this.destroyed && !!this.video
  }

  load(asset: AssetDescription): Promise<void> {
    const hlsUrl = asset.sourceConfig['hls'] as string | undefined
    if (!hlsUrl) {
      return Promise.resolve()
    }

    this.clearLoadHandlers()

    return new Promise<void>((resolve, reject) => {
      this.hls.loadSource(hlsUrl)

      this.hls.once(Hls.Events.MANIFEST_PARSED, () => {
        resolve()
      })

      this.hls.once(Hls.Events.ERROR, (_event, data) => {
        reject(new Error(`HLS load failed (fatal=${data.fatal}): ${data.details}`))
      })
    })
  }

  /**
   * Removes pending MANIFEST_PARSED handlers from any in-flight load.
   * Only clears MANIFEST_PARSED (not ERROR) because the ERROR handler in
   * HlsPlayer.tsx must remain active for fatal error recovery.
   * The ERROR once() handlers are safe to leave — they are registered with
   * once() so they auto-remove after firing.
   */
  private clearLoadHandlers(): void {
    this.hls.removeAllListeners(Hls.Events.MANIFEST_PARSED)
  }

  unload(): Promise<void> {
    if (!this.isReady()) return Promise.resolve()
    this.hls.stopLoad()
    this.video.removeAttribute('src')
    this.video.load()
    return Promise.resolve()
  }

  seekToSMPTE(smpteString: string): void {
    if (!this.isReady()) return
    // Convert HH:MM:SS:FF to seconds, including the frame offset.
    // The HTML5 video element does not guarantee frame-accurate seeking —
    // browsers may snap to the nearest keyframe — but including the frame
    // offset gets us as close as the platform allows.
    const parts = smpteString.split(':')
    if (parts.length < 3) return
    const hours = Number(parts[0])
    const minutes = Number(parts[1])
    const seconds = Number(parts[2])
    const frames = parts.length >= 4 ? Number(parts[3]) : 0
    const frameRate = this.asset.frameRate || DEFAULT_FRAME_RATE
    this.video.currentTime =
      hours * Durations.ONE_HOUR_IN_SECONDS + minutes * Durations.ONE_MINUTE_IN_SECONDS + seconds + frames / frameRate
  }

  getCurrentSmpte(): string | undefined {
    if (!this.isReady()) return undefined
    const time = this.video.currentTime
    const hours = Math.floor(time / Durations.ONE_HOUR_IN_SECONDS)
    const minutes = Math.floor((time % Durations.ONE_HOUR_IN_SECONDS) / Durations.ONE_MINUTE_IN_SECONDS)
    const seconds = Math.floor(time % Durations.ONE_MINUTE_IN_SECONDS)
    const frames = Math.floor((time % 1) * (this.asset.frameRate || DEFAULT_FRAME_RATE))
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}:${pad(frames)}`
  }

  getCurrentTime(): number {
    if (!this.isReady()) return 0
    return this.video.currentTime
  }

  step(stepSize: number): void {
    if (!this.isReady()) return
    const frameDuration = 1 / (this.asset.frameRate || DEFAULT_FRAME_RATE)
    this.video.currentTime += stepSize * frameDuration
  }

  play(): void {
    if (!this.isReady()) return
    this.video.play().catch((e: DOMException) => {
      // NotAllowedError: browser blocked autoplay (expected, not an error)
      // AbortError: play() was interrupted by a new load or pause (expected during rapid interactions)
      if (e.name === 'NotAllowedError' || e.name === 'AbortError') return

      logger().error('HlsPlayerController: Unexpected error during play', { errorName: e.name }, e)
    })
  }

  pause(): void {
    if (!this.isReady()) return
    this.video.pause()
  }

  seek(seekTo: number): void {
    if (!this.isReady()) return
    this.video.currentTime = seekTo
  }

  enterFullscreen(): void {
    if (!this.isReady()) return
    const container = this.video.closest('#hlsjs-player') ?? this.video
    if (container instanceof HTMLElement) {
      container.requestFullscreen?.().catch(() => {
        // Fullscreen not available
      })
    }
  }

  exitFullscreen(): void {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {
        // Exiting fullscreen failed
      })
    }
  }

  isFullscreen(): boolean {
    return !!document.fullscreenElement
  }

  isPlaying(): boolean {
    if (!this.isReady()) return false
    return !this.video.paused && !this.video.ended
  }

  destroy(): void {
    if (this.destroyed) return
    this.destroyed = true
    // Note: do NOT call this.hls.destroy() here — the HLS.js lifecycle
    // is owned by useHlsInstance, which handles creation and cleanup.
  }

  /**
   * Returns the current volume on a 0–100 scale to match the Bitmovin SmpteController interface.
   * The HTML5 video element uses 0–1 internally, so the value is scaled up.
   */
  getVolume(): number {
    if (!this.isReady()) return 0
    return this.video.volume * 100
  }

  /**
   * Sets the volume. Expects a value on a 0–100 scale (matching the Bitmovin SmpteController interface).
   * The value is converted to the HTML5 video element's 0–1 range and clamped to prevent out-of-bounds errors.
   */
  setVolume(volume: number): void {
    if (!this.isReady()) return
    // Bitmovin uses 0-100 for volume; HTML5 video uses 0-1. Convert and clamp to valid range.
    this.video.volume = Math.max(0, Math.min(1, volume / 100))
  }

  mute(): void {
    if (!this.isReady()) return
    this.video.muted = true
  }

  unmute(): void {
    if (!this.isReady()) return
    this.video.muted = false
  }

  isMuted(): boolean {
    if (!this.isReady()) return false
    return this.video.muted
  }

  getPlaybackSpeed(): number {
    if (!this.isReady()) return 1
    return this.video.playbackRate
  }

  setPlaybackSpeed(speed: number): void {
    if (!this.isReady()) return
    this.video.playbackRate = speed
  }

  // --- Audio track methods ---

  getAvailableAudio(): AudioTrack[] {
    if (!this.isReady()) return []
    return this.hls.audioTracks.map((track) => ({
      id: String(track.id),
      label: track.name || track.lang || `Track ${track.id}`,
      lang: track.lang || '',
      getQualities: () => [],
    }))
  }

  getCurrentAudioId(): string {
    if (!this.isReady()) return ''
    const activeIndex = this.hls.audioTrack
    const activeTrack = this.hls.audioTracks[activeIndex]
    return activeTrack ? String(activeTrack.id) : ''
  }

  setAudio(audioId: string): void {
    if (!this.isReady()) return
    const index = this.hls.audioTracks.findIndex((t) => String(t.id) === audioId)
    if (index >= 0) {
      this.hls.audioTrack = index
    }
  }

  // --- Subtitle track methods ---

  getSubtitleList(): SubtitleTrack[] {
    if (!this.isReady()) return []
    const activeIndex = this.hls.subtitleTrack
    return this.hls.subtitleTracks.map((track, index) => ({
      id: String(track.id),
      label: track.name || track.lang || `Track ${track.id}`,
      lang: track.lang || '',
      kind: track.type || 'subtitles',
      enabled: index === activeIndex,
    }))
  }

  enableSubTitle(subtitleTrackId: string): void {
    if (!this.isReady()) return
    const index = this.hls.subtitleTracks.findIndex((t) => String(t.id) === subtitleTrackId)
    if (index >= 0) {
      this.hls.subtitleTrack = index
    }
  }

  disableSubTitle(): void {
    if (!this.isReady()) return
    this.hls.subtitleTrack = -1
  }

  // --- Quality level methods ---

  getAvailableQualities(): QualityLevel[] {
    if (!this.isReady()) return []
    return this.hls.levels.map((level, index) => ({
      index,
      label: level.height ? toStandardQualityLabel(level.height) : `${Math.round(level.bitrate / 1000)} kbps`,
      bitrate: level.bitrate,
      height: level.height ?? 0,
    }))
  }

  getCurrentQualityIndex(): number {
    if (!this.isReady()) return -1
    return this.hls.currentLevel
  }

  setQuality(levelIndex: number): void {
    if (!this.isReady()) return
    // -1 = auto, 0+ = manual selection
    this.hls.currentLevel = levelIndex
  }
}

// --- Helpers ---

/** Standard streaming quality tiers in descending order. */
const STANDARD_QUALITY_TIERS = [2160, 1440, 1080, 720, 480, 360, 240, 144] as const

/**
 * Maps an actual resolution height to the nearest standard quality label.
 * E.g., 534 → "480p", 1072 → "1080p", 396 → "360p".
 */
function toStandardQualityLabel(height: number): string {
  let closest: number = STANDARD_QUALITY_TIERS[0]
  let minDiff = Math.abs(height - closest)
  for (const tier of STANDARD_QUALITY_TIERS) {
    const diff = Math.abs(height - tier)
    if (diff < minDiff) {
      minDiff = diff
      closest = tier
    }
  }
  return `${closest}p`
}
